import { Drawer, List, ListItem, Typography, useMediaQuery, useTheme, Box } from '@mui/material';
import { business } from 'routes/config';
import SidebarItem from './SidebarItem';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProfileIcon from 'assets/icons/ProfileIcon';
import BusinessTeamIcon from '@/assets/icons/BusinessTeamIcon';
import ContactsIcon from 'assets/icons/ContactsIcon';
import KnowledgeIcon from 'assets/icons/KnowledgeIcon';
import IntegrationIcon from 'assets/icons/IntegrationIcon';
import TelephonyApiKeyIcon from '@/assets/icons/TelephonyApiKeyIcon';
import UsageProgressCard from './UsageProgressCard';
import BillingIcon from '@/assets/icons/BillingIcon';
import BusinessUsageIcon from '@/assets/icons/BusinessUsageIcon';
import LocationIcon from '@/assets/icons/LocationIcon';
import AuditTrailsIcon from '@/assets/icons/AuditTrailsIcon';
import TelephonyIcon from '@/assets/icons/TelephonyIcon';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { useGetCurrentUser } from '@/apis/user/hook';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';
import BusinessDropdown from './BusinessDropdown';
import { RxCross2 } from 'react-icons/rx';
import WorkflowSidebarIcon from '@/assets/icons/WorkflowSidebarIcon';
import AddonsIcon from '@/assets/icons/AddonsIcon';
import { useAiWorkflowAddonActive } from '@/pages/Business/Setting/hooks/useAiWorkflowAddonActive';

interface Props {
  isMenuOpen?: boolean;
  handleClose?: () => void;
  disabled?: boolean;
}

export default function BusinessSidebar({ isMenuOpen, handleClose, disabled }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: businessProfile } = useGetBusinessProfile();
  const { data: currentUser } = useGetCurrentUser();
  const { aiWorkflowPresent } = useAiWorkflowAddonActive();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLocationAdmin = currentUser?.role === 'location_admin';

  const handleWorkflowsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!location.pathname.includes('/workflows')) return;
    event.preventDefault();
    navigate(`${business.base}/${business.workflows}`, {
      replace: true,
      state: { embedKey: Date.now() }
    });
  };

  return (
    <Drawer
      variant={isMediumScreen ? 'temporary' : 'permanent'}
      open={isMediumScreen ? isMenuOpen : true}
      onClose={handleClose}
      anchor='left'
      sx={{
        opacity: disabled ? 0.5 : 1,
        width: 260,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 260,
          boxSizing: 'border-box',
          top: isMediumScreen ? 0 : '65px',
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      <Box display='flex' flexDirection='column' height='calc(100vh - 64px)'>
        <Box flex={1} overflow='auto' sx={{ scrollbarWidth: 'thin' }}>
          <List>
            <ListItem sx={{ fontSize: '22px' }}>
              {isMediumScreen ? (
                <Box pb={2} borderBottom='1px solid #EAEAEA'>
                  <Box display='flex' alignItems='center' gap={2}>
                    <Link to='/' style={{ textDecoration: 'none' }}>
                      <GoBackButtonIcon />
                    </Link>
                    <BusinessDropdown />
                    <Box>
                      <RxCross2 size={18} onClick={handleClose} />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Link to='/' style={{ textDecoration: 'none' }}>
                  <Box display='flex' flexDirection='row' alignItems='center'>
                    <GoBackButtonIcon />
                    <Typography variant='body2' ml={1}>
                      Back
                    </Typography>
                  </Box>
                </Link>
              )}
            </ListItem>

            <SidebarItem to={business.usage} icon={<BusinessUsageIcon />} label='Usage' disabled={disabled} />
            <SidebarItem to={business.profile} icon={<ProfileIcon />} label='Business Profile' disabled={disabled} />
            {!isLocationAdmin && (
              <>
                <SidebarItem to={business.locations} icon={<LocationIcon />} label='Locations' disabled={disabled} />
                <SidebarItem to={business.team} icon={<BusinessTeamIcon />} label='Team' disabled={disabled} />
              </>
            )}
            <SidebarItem to={business.contacts} icon={<ContactsIcon />} label='Contacts' disabled={disabled} />
            <SidebarItem
              to={business.KnowledgeBases}
              icon={<KnowledgeIcon />}
              label='Knowledge Base'
              disabled={disabled}
            />
            <SidebarItem
              to={business.integration}
              icon={<IntegrationIcon />}
              label='Integrations'
              disabled={disabled}
            />
            {aiWorkflowPresent && (
              <SidebarItem
                to={business.workflows}
                icon={<WorkflowSidebarIcon />}
                label={
                  <Box display='flex' alignItems='center' gap={0.75}>
                    AI Workflows
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        color: theme.palette.error.main,
                        backgroundColor: theme.palette.error.light,
                        border: `1px solid ${theme.palette.error.main}`,
                        borderRadius: '100px',
                        px: 0.8,
                        py: 0.35,
                        lineHeight: 1.2
                      }}
                    >
                      BETA
                    </Typography>
                  </Box>
                }
                disabled={disabled}
                onClick={handleWorkflowsClick}
              />
            )}
            <SidebarItem to={business.apiKeys} icon={<TelephonyApiKeyIcon />} label='API Keys' disabled={disabled} />
            <SidebarItem
              to={`${business.base}/${business.telephony}/numbers`}
              icon={<TelephonyIcon />}
              label='Telephony'
              disabled={disabled}
            />
            <SidebarItem to={business.addons} icon={<AddonsIcon />} label='Add-ons' disabled={disabled} />
            {!businessProfile?.hide_billing && businessProfile?.business_plan_id && (
              <SidebarItem to={business.billing} icon={<BillingIcon />} label='Billing' />
            )}
            <SidebarItem
              to={business.auditTrails}
              icon={<AuditTrailsIcon />}
              label='Audit Trails'
              disabled={disabled}
            />
          </List>
        </Box>
        {!businessProfile?.hide_billing && businessProfile?.business_plan_id && (
          <Box pb={1} px={2}>
            <UsageProgressCard />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
